/**
 * Custom hook for managing analysis job state and WebSocket events
 * Extracts all the WebSocket event handling and state management from AnalysisJobPage
 */

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useSocket } from '../../contexts/socketTypes';
import { useAuthenticatedApi } from '../useAuthenticatedApi';
import { logger } from '../../utils/logger';
import type { JobStatus, UseAnalysisJobReturn } from './types';

import {
  createAgentProgressHandler,
  createCompleteHandler,
  createErrorHandler,
  createFailedHandler,
  createProgressHandler,
} from './socketHandlers';

const STALE_DATA_THRESHOLD_MS = 30000;
const DISCONNECT_BANNER_DELAY_MS = 5000;
const JOIN_ROOM_TIMEOUT_MS = 2000;

export function useAnalysisJob(jobId: string | undefined): UseAnalysisJobReturn {
  const api = useAuthenticatedApi();
  const { socket, isConnected, hasConnectedOnce, reconnectCount, joinJob, leaveJob } = useSocket();

  // State
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [showDisconnectedBanner, setShowDisconnectedBanner] = useState(false);

  // Refs
  const lastProgressUpdateRef = useRef(Date.now());
  const hasFetchedRef = useRef(false);
  const currentJobIdRef = useRef<string | null>(null);
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSeqRef = useRef<number>(-1);
  const outOfOrderCountRef = useRef<number>(0);
  const roomJoinedRef = useRef<boolean>(false);

  const touchProgress = useCallback(() => {
    lastProgressUpdateRef.current = Date.now();
  }, []);

  // Reset state when jobId changes
  useEffect(() => {
    if (jobId !== currentJobIdRef.current) {
      hasFetchedRef.current = false;
      currentJobIdRef.current = jobId || null;
      lastSeqRef.current = -1;
      outOfOrderCountRef.current = 0;
    }
    setJobStatus(null);
    setLoading(true);
    setError(null);
  }, [jobId]);

  // Handle delayed disconnection banner
  useEffect(() => {
    if (!hasConnectedOnce) return;

    if (!isConnected) {
      disconnectTimerRef.current = setTimeout(() => {
        setShowDisconnectedBanner(true);
      }, DISCONNECT_BANNER_DELAY_MS);
    } else {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      setShowDisconnectedBanner(false);
    }

    return () => {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
    };
  }, [isConnected, hasConnectedOnce]);

  // Join/leave job room - track join status for fetch sequencing
  useEffect(() => {
    if (!jobId || !isConnected) return;

    roomJoinedRef.current = false;
    joinJob(jobId).then((result) => {
      roomJoinedRef.current = result.success;
    });

    return () => {
      roomJoinedRef.current = false;
      leaveJob(jobId);
    };
  }, [jobId, isConnected, joinJob, leaveJob]);

  // Refetch data when socket reconnects
  useEffect(() => {
    if (!jobId || reconnectCount === 0) return;

    void (async () => {
      try {
        const response = await api.get(`/analysis/jobs/${jobId}`);
        const status: JobStatus = response.data;

        setJobStatus(status);
        touchProgress();
        lastSeqRef.current = -1;
        outOfOrderCountRef.current = 0;
      } catch (err) {
        logger.error('[RECONNECT] Failed to refetch status:', { error: err });
      }
    })();
  }, [reconnectCount, jobId, api, touchProgress]);

  // Refetch data when user returns to tab with stale WS data (>30s since last message)
  useEffect(() => {
    if (!jobId) return;
    let mounted = true;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      // Skip refresh once the job is in a terminal state — the result is
      // final, no progress updates are coming, and the elapsed-since-last-
      // progress threshold would always trip. Only refresh while the job
      // is still active.
      const status = jobStatus?.status;
      if (status !== 'running' && status !== 'pending' && status !== 'queued') return;

      const elapsed = Date.now() - lastProgressUpdateRef.current;
      if (elapsed <= STALE_DATA_THRESHOLD_MS) return;

      logger.info('[AnalysisJob] Tab returned with stale data, refreshing', {
        elapsedMs: elapsed,
      });

      void (async () => {
        try {
          const response = await api.get(`/analysis/jobs/${jobId}`);
          if (!mounted) return;
          const status: JobStatus = response.data;
          setJobStatus(status);
          touchProgress();
          lastSeqRef.current = -1;
          outOfOrderCountRef.current = 0;
        } catch (err) {
          logger.error('[AnalysisJob] Failed to refresh on tab return:', { error: err });
        }
      })();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [jobId, api, touchProgress, jobStatus?.status]);

  // Fetch initial job status and set up WebSocket listeners
  useEffect(() => {
    if (!jobId || !socket) return;

    let cancelled = false;

    const fetchInitialStatus = async () => {
      // Wait for room join to complete (with timeout) before fetching initial status.
      // This prevents the race condition where events are emitted between fetch and join.
      const joinDeadline = Date.now() + JOIN_ROOM_TIMEOUT_MS;
      while (!roomJoinedRef.current && Date.now() < joinDeadline) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        if (cancelled) return;
      }
      if (cancelled) return;

      // Retry logic for initial status fetch
      // Job may not exist in DB immediately after creation (transaction pending)
      const MAX_RETRIES = 5;
      const BASE_DELAY_MS = 500;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        if (cancelled) return;

        try {
          const response = await api.get(`/analysis/jobs/${jobId}`);
          if (cancelled) return;

          const status: JobStatus = response.data;
          setJobStatus(status);
          setLoading(false);
          touchProgress();
          return; // Success - exit retry loop
        } catch (err: unknown) {
          if (cancelled) return;

          const status = axios.isAxiosError(err) ? err.response?.status : undefined;

          // 404 means job not in DB yet - retry with backoff
          if (status === 404 && attempt < MAX_RETRIES) {
            const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
            logger.warn(
              `[useAnalysisJob] Job not found (attempt ${attempt}/${MAX_RETRIES}), retrying in ${delay}ms`,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          // 401/403 are auth errors - don't retry, let interceptor handle
          if (status === 401 || status === 403) {
            setError('Authentication required');
            setLoading(false);
            return;
          }

          // 5xx server errors - retry with backoff
          if (status != null && status >= 500 && attempt < MAX_RETRIES) {
            const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
            logger.warn(`[useAnalysisJob] Server error (${status}), retrying in ${delay}ms`);
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          // Final attempt or non-retryable error
          logger.error('[useAnalysisJob] Failed to fetch job status:', { error: err });
          setError('Failed to load analysis status. Please try refreshing the page.');
          setLoading(false);
          return;
        }
      }
    };

    fetchInitialStatus();
    lastProgressUpdateRef.current = Date.now();

    const handlerDeps = {
      jobId,
      api,
      setJobStatus,
      touchProgress,
      lastSeqRef,
      outOfOrderCountRef,
    };

    const handleProgress = createProgressHandler(handlerDeps);
    const handleComplete = createCompleteHandler(handlerDeps);
    const handleError = createErrorHandler(handlerDeps);
    const handleFailed = createFailedHandler(handlerDeps);
    const handleAgentProgress = createAgentProgressHandler(handlerDeps);

    socket.on('analysis-progress', handleProgress);
    socket.on('analysis-complete', handleComplete);
    socket.on('analysis-error', handleError);
    socket.on('analysis-failed', handleFailed);
    socket.on('agent-progress', handleAgentProgress);

    return () => {
      cancelled = true;
      socket.off('analysis-progress', handleProgress);
      socket.off('analysis-complete', handleComplete);
      socket.off('analysis-error', handleError);
      socket.off('analysis-failed', handleFailed);
      socket.off('agent-progress', handleAgentProgress);
    };
  }, [jobId, socket, api, touchProgress]);

  // Track elapsed time
  useEffect(() => {
    if (jobStatus?.status === 'running' && jobStatus?.startedAt) {
      const startTime = new Date(jobStatus.startedAt).getTime();

      const interval = setInterval(() => {
        const now = Date.now();
        const elapsedMs = now - startTime;
        setElapsedSeconds(Math.floor(elapsedMs / 1000));
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [jobStatus?.status, jobStatus?.startedAt]);

  // Calculate progress percentage
  const calculatedPercent = useMemo(() => {
    if (!jobStatus) return 0;

    const basePercent = jobStatus.workflow?.percent ?? jobStatus.progress?.percent ?? 0;

    if (
      jobStatus.agentProgress?.stepProgress !== undefined &&
      jobStatus.agentProgress.stepProgress > 0
    ) {
      const currentStepId = jobStatus.workflow?.steps?.find((s) => s.status === 'running')?.id;

      if (
        currentStepId === 'analyzing_repo' ||
        currentStepId === 'running_agents' ||
        currentStepId === 'llm_analysis'
      ) {
        const agentStepPercent = 60;
        const completionCredit = agentStepPercent * 0.9 * jobStatus.agentProgress.stepProgress;
        return Math.min(99, Math.round(basePercent + completionCredit));
      }
    }

    return Math.min(99, Math.max(0, basePercent));
  }, [jobStatus]);

  // High-water mark: progress never decreases for a given job. Tracked in
  // state (not a ref mutated during render) so concurrent-mode renders
  // remain deterministic.
  const [progressPercent, setProgressPercent] = useState(0);
  useEffect(() => {
    setProgressPercent(0);
  }, [jobId]);
  useEffect(() => {
    setProgressPercent((prev) => Math.max(prev, calculatedPercent));
  }, [calculatedPercent]);

  // Handlers
  const handleCancel = useCallback(async () => {
    if (!jobId || !jobStatus) return;

    try {
      setCancelling(true);
      await api.delete(`/analysis/jobs/${jobId}`);
      setJobStatus({ ...jobStatus, status: 'cancelled' });
    } catch {
      setError('Failed to cancel analysis');
    } finally {
      setCancelling(false);
    }
  }, [jobId, jobStatus, api]);

  const handleRetry = useCallback(async (): Promise<string | null> => {
    try {
      setRetrying(true);
      setError(null);

      const response = await api.post(`/analysis/retry/${jobId}`);
      const { jobId: newJobId } = response.data;
      return newJobId;
    } catch {
      setError('Failed to start new analysis. Please try again.');
      return null;
    } finally {
      setRetrying(false);
    }
  }, [jobId, api]);

  return {
    jobStatus,
    loading,
    error,
    cancelling,
    retrying,
    showDisconnectedBanner,
    elapsedSeconds,
    progressPercent,
    handleCancel,
    handleRetry,
    setError,
  };
}
